import { useState } from 'react';
import { Badge, PhotoThumb } from '@indipia/ui-core';
import { formatReceivedAt } from '@indipia/shared-features';
import { withDevShareId } from '../../api/worklogApi';
import { PhotoLightbox } from '../PhotoLightbox';
import { SectionLabel } from '../WorkOrderNoticeView';
import type { CustReqNoticeItem } from '../../types/custReqNotice.types';
import { CUST_REQ_NOTICE_MESSAGE as MSG, noticeTitle } from './custReqNotice.logic';

/**
 * 반장 작업요청 안내 화면의 '고객 요청 n건' 카드 영역 — 기획 004 B-4(작업요청 상세보기).
 * 순서는 작업 요청내용 → 참고 사진 → 이 영역. 요청마다 번호·원문·받은 경로·접수일·사진(누르면 원본 보기).
 * 요청이 없으면(공정 불확정 포함) 영역 전체를 그리지 않는다. 사진은 공유 쿠키로 직접 로드하며
 * 불러오기 실패는 PhotoThumb 의 오류 표시를 따른다.
 */
export function CustReqNoticeSection({
  items, showGuide = true,
}: {
  items: CustReqNoticeItem[];
  /** 작성 권한이 없는 열람자(참조인·기한 만료)는 체크 안내를 숨긴다 */
  showGuide?: boolean;
}) {
  const [lb, setLb] = useState<{ photos: { url: string }[]; index: number } | null>(null);
  if (items.length === 0) return null;

  return (
    <section
      className="mt-2 px-4 pt-3 pb-4"
      style={{ background: 'var(--shell-surface)', borderTop: 'var(--border-width-hairline) solid var(--shell-border)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)' }}
    >
      <div className="mb-1">
        <SectionLabel>{noticeTitle(items.length)}</SectionLabel>
      </div>
      {showGuide && (
        <p className="mb-2.5 text-caption" style={{ color: 'var(--shell-hint)' }}>{MSG.guide}</p>
      )}
      <ul className="space-y-2">
        {items.map((r, idx) => (
          <li
            key={r.custReqId}
            className="p-3"
            style={{ background: 'var(--gt-subtle)', border: 'var(--border-width-hairline) solid var(--gt-subtle)', borderRadius: 'var(--radius-md)' }}
          >
            <div className="flex items-center justify-between gap-2">
              <Badge status="accent" className="!rounded-full">{idx + 1}</Badge>
              <span className="text-caption" style={{ color: 'var(--shell-hint)' }}>
                {[r.srcName, formatReceivedAt(r.receivedAt)].filter(Boolean).join(' · ')}
              </span>
            </div>
            <p
              className="mt-2 whitespace-pre-line text-body-sm"
              style={{ color: 'var(--shell-on-surface)', lineHeight: 1.7, wordBreak: 'break-word' }}
            >
              {r.content}
            </p>
            {r.photos.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {r.photos.map((p, pi) => (
                  <PhotoThumb
                    key={p.photoId}
                    size="md"
                    src={withDevShareId(p.thumbUrl)}
                    alt={`${MSG.requestPhoto} ${pi + 1}`}
                    onOpen={() => setLb({ photos: r.photos.map((q) => ({ url: withDevShareId(q.url || q.thumbUrl) as string })), index: pi })}
                  />
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
      <PhotoLightbox
        photos={lb?.photos ?? []}
        index={lb?.index ?? null}
        onClose={() => setLb(null)}
        onIndexChange={(i) => setLb((prev) => (prev ? { ...prev, index: i } : prev))}
      />
    </section>
  );
}
